import { TOKEN_KEY, REFRESH_TOKEN_KEY } from '@constants/token';

/**
 * tokenStorage
 *
 * A small utility to manage access/refresh tokens for both
 * "Remember me" (localStorage) and session-based (sessionStorage) logins.
 *
 * Policy:
 * - If refreshToken exists in localStorage → user chose "Remember me"
 *   → accessToken should also live in localStorage.
 * - Otherwise, tokens are stored in sessionStorage.
 */
export const tokenStorage = {
  /**
   * Returns the current access token.
   * Checks localStorage first, then sessionStorage.
   */
  getAccessToken(): string | null {
    return (
      window.localStorage.getItem(TOKEN_KEY) ??
      window.sessionStorage.getItem(TOKEN_KEY)
    );
  },

  /**
   * Returns the current refresh token.
   * Checks localStorage first, then sessionStorage.
   */
  getRefreshToken(): string | null {
    return (
      window.localStorage.getItem(REFRESH_TOKEN_KEY) ??
      window.sessionStorage.getItem(REFRESH_TOKEN_KEY)
    );
  },

  /**
   * Indicates whether the refresh token is stored in localStorage.
   * This effectively means "Remember me" is enabled.
   */
  isRefreshStoredInLocal(): boolean {
    return !!window.localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Stores the access token in the same storage as the refresh token.
   * This keeps access/refresh tokens aligned with the user's session policy.
   */
  setAccessToken(token: string) {
    if (this.isRefreshStoredInLocal()) {
      window.localStorage.setItem(TOKEN_KEY, token);
    } else {
      window.sessionStorage.setItem(TOKEN_KEY, token);
    }
  },

  /**
   * Clears all authentication tokens from both storages.
   * Used on logout or when the session is considered invalid/expired.
   */
  clearAll() {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    window.sessionStorage.removeItem(TOKEN_KEY);
    window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};
